import { useState } from 'react'
import { fbt } from 'fbtee'
import { Flag } from 'lucide-react'
import { ProblemReportDialog } from '../../components/ProblemReportDialog'
import { useAuth } from '../../hooks/useAuth'
import { useEventReports } from '../../hooks/useEventReports'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { eventReportCategoryLabel } from '../../lib/eventReportLabels'
import { reportOtherNotePlaceholder, reportSentToast } from '../../lib/commonStrings'
import {
  EVENT_REPORT_CATEGORIES,
  EVENT_REPORT_NOTE_REQUIRED,
  type EventReportCategory,
} from '../../types'

/**
 * The quiet way out of an event page — deliberately last, wherever the page
 * ends, so it never competes with anything you'd actually come here to do.
 */
export function ReportEventButton({ eventId }: { eventId: string }) {
  const { signedInUid } = useAuth()
  const { guard } = useGuestGuard()
  const { submitReport, pendingCategories } = useEventReports(eventId, signedInUid)
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        data-testid="event-report-open"
        onClick={guard(() => setOpen(true), {
          intent: 'report',
          title: fbt('Log in to report events', 'Guest upsell title when tapping report event'),
          description: fbt(
            'Create an account to help us keep event listings accurate.',
            'Guest upsell description when tapping report event',
          ),
        })}
        className="flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground pt-4 cursor-pointer"
      >
        <Flag className="w-3 h-3" />
        <fbt desc="Small link at the bottom of event info to report the event listing">
          Report event
        </fbt>
      </button>

      <ProblemReportDialog<EventReportCategory>
        open={open}
        onOpenChange={setOpen}
        description={fbt(
          "We'll check what you flag against the event's own page and fix it.",
          'Description in the event report dialog',
        )}
        categories={EVENT_REPORT_CATEGORIES}
        categoryLabel={eventReportCategoryLabel}
        pendingCategories={pendingCategories}
        noteRequired={c => EVENT_REPORT_NOTE_REQUIRED.includes(c)}
        notePlaceholder={c =>
          c === 'other'
            ? reportOtherNotePlaceholder()
            : fbt(
                'Details, e.g. the new venue or date (optional)',
                'Placeholder for the optional note on an event report',
              )
        }
        withLink
        onSubmit={submitReport}
        successToast={reportSentToast()}
      />
    </>
  )
}
